import {RowData, TableMeta} from '@tanstack/react-table';

/**
 * Augment this interface wherever you use react-table to get type-safe meta access
 * To augment this interface, you can use this snippet:
 * ```
 * declare module '../../helpers/reactTableMeta' {
 *   interface ReactTableMetaValueMap {
 *     'my-table': {
 *       addColumn: () => void;
 *       addRow: () => void;
 *     };
 *   }
 * }
 * ```
 * */
export interface ReactTableMetaValueMap {}
type ReactTableMetaType = keyof ReactTableMetaValueMap;

// See https://tanstack.com/table/v8/docs/api/core/table#meta.
declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    type: ReactTableMetaType;
    value: ReactTableMetaValueMap[ReactTableMetaType];
  }
}

export function getReactTableMeta<T extends ReactTableMetaType>(
  table: {options: {meta?: TableMeta<any>}},
  type: T,
): ReactTableMetaValueMap[T] {
  if (table.options.meta?.type !== type) {
    throw new Error('react-table was instantiated with the wrong metadata');
  }

  return table.options.meta?.value as ReactTableMetaValueMap[T];
}

export function makeReactTableMeta<T extends ReactTableMetaType>(type: T, value: ReactTableMetaValueMap[T]) {
  return {
    type,
    value,
  };
}
